import PropTypes from 'prop-types';
import { useState } from 'react';

import CloseOutlined from '@ant-design/icons/CloseOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SLOT_DURATIONS = [5, 10, 15, 20, 30, 45, 60];

const DEFAULT_RANGE = { start_time: '09:00', end_time: '17:00' };

// Times are stored as "HH:MM"; saved profiles may come back as "HH:MM:SS".
const toHHMM = (value) => (value ? String(value).slice(0, 5) : '');
const toMinutes = (value) => {
  const [hours, minutes] = toHHMM(value).split(':').map(Number);
  return hours * 60 + minutes;
};
const fromMinutes = (total) => {
  const clamped = Math.max(0, Math.min(total, 23 * 60 + 45));
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
};
const formatTime = (value) => (value ? dayjs(`2000-01-01T${toHHMM(value)}`).format('h:mm A') : '');

const TIME_OPTIONS = Array.from({ length: 96 }, (_, index) => fromMinutes(index * 15));
const normalizeForSearch = (text) => text.toLowerCase().replace(/[^0-9apm]/g, '');

const PRESETS = [
  { label: 'Mon–Fri, 9 AM – 5 PM', weekdays: [0, 1, 2, 3, 4] },
  { label: 'Mon–Sat, 9 AM – 5 PM', weekdays: [0, 1, 2, 3, 4, 5] },
  { label: 'Every day, 9 AM – 5 PM', weekdays: [0, 1, 2, 3, 4, 5, 6] }
];

// Searchable 15-minute picker: typing "930" or "2p" narrows the list.
function TimeSelect({ label, value, onChange, minTime, error }) {
  const current = toHHMM(value);
  const options = TIME_OPTIONS.filter((option) => !minTime || toMinutes(option) > toMinutes(minTime));
  if (current && !options.includes(current)) options.push(current);
  options.sort((first, second) => toMinutes(first) - toMinutes(second));

  return (
    <Autocomplete
      size="small"
      disableClearable
      autoHighlight
      options={options}
      value={current || null}
      getOptionLabel={formatTime}
      filterOptions={(list, { inputValue }) => {
        const query = normalizeForSearch(inputValue);
        return query ? list.filter((option) => normalizeForSearch(formatTime(option)).includes(query)) : list;
      }}
      onChange={(_event, next) => next && onChange(next)}
      ListboxProps={{ style: { maxHeight: 240 } }}
      sx={{ width: 132 }}
      renderInput={(params) => <TextField {...params} label={label} error={error} />}
    />
  );
}

TimeSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  minTime: PropTypes.string,
  error: PropTypes.bool
};

function TimeRange({ range, onChange, onRemove, removeLabel }) {
  const handleStart = (start) => {
    // Keep the range valid: push the end an hour past a start that overtakes it.
    const end = range.end_time && toMinutes(range.end_time) > toMinutes(start) ? range.end_time : fromMinutes(toMinutes(start) + 60);
    onChange({ ...range, start_time: start, end_time: end });
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TimeSelect label="From" value={range.start_time} onChange={handleStart} />
      <Typography color="text.secondary">–</Typography>
      <TimeSelect
        label="To"
        value={range.end_time}
        minTime={range.start_time}
        onChange={(end) => onChange({ ...range, end_time: end })}
      />
      {onRemove && (
        <IconButton size="small" aria-label={removeLabel} onClick={onRemove}>
          <CloseOutlined />
        </IconButton>
      )}
    </Stack>
  );
}

TimeRange.propTypes = {
  range: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  removeLabel: PropTypes.string
};

export default function DoctorBookingFields({ booking, errors, setFieldValue, onPhotoChange }) {
  const today = new Date();
  const minimumOverrideDate = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0')
  ].join('-');
  const currentYear = new Date().getFullYear();
  const practiceStartYear = Number(booking.practice_start_year);
  const calculatedExperience =
    practiceStartYear >= 1900 && practiceStartYear <= currentYear
      ? currentYear - practiceStartYear
      : null;
  const setBookingField = (field, value) => setFieldValue(`doctorBooking.${field}`, value);

  const [copySource, setCopySource] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);
  const [presetAnchor, setPresetAnchor] = useState(null);

  // ---- weekly availability, edited per weekday ----
  const weeklyRanges = booking.weekly_availability.filter((range) => range.weekday !== '' && range.weekday !== null);
  const rangesFor = (weekday) => weeklyRanges.filter((range) => Number(range.weekday) === weekday);
  const saveWeekly = (ranges) => {
    const sorted = [...ranges].sort(
      (first, second) => first.weekday - second.weekday || toMinutes(first.start_time) - toMinutes(second.start_time)
    );
    setBookingField('weekly_availability', sorted);
  };
  const setDayRanges = (weekday, ranges) => saveWeekly([
    ...weeklyRanges.filter((range) => Number(range.weekday) !== weekday),
    ...ranges.map((range) => ({ weekday, start_time: toHHMM(range.start_time), end_time: toHHMM(range.end_time) }))
  ]);
  const lastConfiguredRanges = () => {
    const configured = weeklyRanges[weeklyRanges.length - 1];
    return configured ? rangesFor(Number(configured.weekday)) : [DEFAULT_RANGE];
  };
  const addRangeToDay = (weekday) => {
    const ranges = rangesFor(weekday);
    const last = ranges[ranges.length - 1];
    const start = last ? Math.min(toMinutes(last.end_time) + 60, 22 * 60) : 9 * 60;
    setDayRanges(weekday, [...ranges, { start_time: fromMinutes(start), end_time: fromMinutes(start + 120) }]);
  };
  const openCopy = (event, weekday) => {
    setCopySource({ anchor: event.currentTarget, weekday });
    setCopyTargets([]);
  };
  const applyCopy = () => {
    const sourceRanges = rangesFor(copySource.weekday);
    saveWeekly([
      ...weeklyRanges.filter((range) => !copyTargets.includes(Number(range.weekday))),
      ...copyTargets.flatMap((weekday) => sourceRanges.map((range) => ({ ...range, weekday })))
    ]);
    setCopySource(null);
  };
  const applyPreset = (preset) => {
    saveWeekly(preset.weekdays.map((weekday) => ({ weekday, ...DEFAULT_RANGE })));
    setPresetAnchor(null);
  };

  // ---- date overrides ----
  const overrides = booking.date_overrides;
  const saveOverrides = (next) => setBookingField('date_overrides', next);
  const updateOverride = (index, changes) => saveOverrides(
    overrides.map((override, itemIndex) => (itemIndex === index ? { ...override, ...changes } : override))
  );
  const nextFreeDate = (fromDate) => {
    const taken = new Set(overrides.map((override) => override.date));
    let candidate = fromDate ? dayjs(fromDate).add(1, 'day') : dayjs();
    while (taken.has(candidate.format('YYYY-MM-DD'))) candidate = candidate.add(1, 'day');
    return candidate.format('YYYY-MM-DD');
  };
  const addOverride = () => saveOverrides([
    ...overrides,
    { date: nextFreeDate(overrides[overrides.length - 1]?.date), is_closed: true, start_time: '', end_time: '' }
  ]);
  const duplicateOverride = (index) => {
    const source = overrides[index];
    const copy = { ...source, id: undefined, date: nextFreeDate(source.date) };
    saveOverrides([...overrides.slice(0, index + 1), copy, ...overrides.slice(index + 1)]);
  };

  return (
    <Grid item xs={12}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
        >
          <Box>
            <Typography variant="h5">Online booking</Typography>
            <Typography variant="body2" color="text.secondary">
              Control whether this doctor appears in patient booking.
            </Typography>
          </Box>
          <FormControlLabel
            label={booking.online_booking_enabled ? 'Accepting bookings' : 'Online booking off'}
            labelPlacement="start"
            sx={{ m: 0, gap: 1 }}
            control={
            <Switch
              checked={booking.online_booking_enabled}
              onChange={(event) => setBookingField('online_booking_enabled', event.target.checked)}
            />
            }
          />
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Practice start year"
              value={booking.practice_start_year}
              inputProps={{ min: 1900, max: currentYear }}
              onChange={(event) => setBookingField('practice_start_year', event.target.value)}
              helperText={
                calculatedExperience === null
                  ? 'Used to calculate experience'
                  : `${calculatedExperience} years of experience`
              }
              FormHelperTextProps={{ sx: { minHeight: 20, mx: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="New patient fee"
              value={booking.consultation_fee}
              inputProps={{ min: 0, step: 1 }}
              onChange={(event) => setBookingField('consultation_fee', event.target.value)}
              helperText="Shown on online booking. Leave empty for the hospital default"
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              FormHelperTextProps={{ sx: { minHeight: 20, mx: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Old patient fee"
              value={booking.old_patient_fee}
              inputProps={{ min: 0, step: 1 }}
              onChange={(event) => setBookingField('old_patient_fee', event.target.value)}
              helperText="Patients registered before today. Leave empty for the hospital default"
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              FormHelperTextProps={{ sx: { minHeight: 20, mx: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Follow-up fee"
              value={booking.follow_up_fee}
              inputProps={{ min: 0, step: 1 }}
              onChange={(event) => setBookingField('follow_up_fee', event.target.value)}
              helperText="Return visits within the hospital's follow-up window. Leave empty for the hospital default"
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              FormHelperTextProps={{ sx: { minHeight: 20, mx: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
                select
                fullWidth
                label="Slot duration"
                value={booking.slot_duration_minutes}
                onChange={(event) => setBookingField('slot_duration_minutes', Number(event.target.value))}
                helperText={
                  booking.recommended_slot_duration
                    ? `Suggested: ${booking.recommended_slot_duration.minutes} min from ${booking.recommended_slot_duration.sample_count} visits${booking.recommended_slot_duration.used_fallback ? ' (fallback)' : ''}`
                    : 'Time reserved for each appointment'
                }
                FormHelperTextProps={{ sx: { minHeight: 20, mx: 0 } }}
              >
                {SLOT_DURATIONS.map((duration) => (
                  <MenuItem key={duration} value={duration}>{duration} minutes</MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Daily booking limit"
              value={booking.max_online_bookings_per_day}
              inputProps={{ min: 1 }}
              onChange={(event) => setBookingField('max_online_bookings_per_day', Number(event.target.value))}
              helperText="Maximum online appointments per day"
              FormHelperTextProps={{ sx: { minHeight: 20, mx: 0 } }}
            />
          </Grid>
        </Grid>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          {booking.profile_photo && (
            <Box
              component="img"
              src={booking.profile_photo}
              alt="Doctor profile"
              sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }}
            />
          )}
          <Box>
            <Button component="label" variant="outlined" startIcon={<UploadOutlined />}>
              {booking.profile_photo ? 'Replace profile photo' : 'Upload profile photo'}
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => onPhotoChange(event.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              JPG, PNG, or WebP
            </Typography>
          </Box>
        </Stack>

        <Divider />
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box>
              <Typography variant="h6">Weekly availability</Typography>
              <Typography variant="caption" color="text.secondary">Regular patient-booking hours</Typography>
            </Box>
            <Button size="small" variant="outlined" onClick={(event) => setPresetAnchor(event.currentTarget)}>
              Quick set
            </Button>
            <Menu anchorEl={presetAnchor} open={Boolean(presetAnchor)} onClose={() => setPresetAnchor(null)}>
              {PRESETS.map((preset) => (
                <MenuItem key={preset.label} onClick={() => applyPreset(preset)}>{preset.label}</MenuItem>
              ))}
              <Divider />
              <MenuItem onClick={() => applyPreset({ weekdays: [] })} sx={{ color: 'error.main' }}>Clear all days</MenuItem>
            </Menu>
          </Stack>
          {typeof errors?.weekly_availability === 'string' && (
            <Typography variant="caption" color="error">{errors.weekly_availability}</Typography>
          )}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {WEEKDAYS.map((day, weekday) => {
              const ranges = rangesFor(weekday);
              const isOpen = ranges.length > 0;
              return (
                <Stack
                  key={day}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={{ xs: 1, md: 2 }}
                  alignItems={{ md: 'flex-start' }}
                  sx={{ px: 1.5, py: 1.25, '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }}
                >
                  <FormControlLabel
                    sx={{ m: 0, width: 150, flexShrink: 0, minHeight: 40 }}
                    control={
                      <Switch
                        size="small"
                        checked={isOpen}
                        onChange={(event) => setDayRanges(weekday, event.target.checked ? lastConfiguredRanges() : [])}
                      />
                    }
                    label={<Typography sx={{ ml: 1, fontWeight: 500 }}>{day}</Typography>}
                  />
                  <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
                    {isOpen ? (
                      ranges.map((range, rangeIndex) => (
                        <TimeRange
                          key={rangeIndex}
                          range={range}
                          onChange={(next) => setDayRanges(weekday, ranges.map((item, itemIndex) => (itemIndex === rangeIndex ? next : item)))}
                          onRemove={() => setDayRanges(weekday, ranges.filter((_, itemIndex) => itemIndex !== rangeIndex))}
                          removeLabel={`Remove ${day} hours`}
                        />
                      ))
                    ) : (
                      <Typography color="text.secondary" sx={{ lineHeight: '40px' }}>Unavailable</Typography>
                    )}
                  </Stack>
                  {isOpen && (
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <Tooltip title="Add another time range">
                        <IconButton size="small" aria-label={`Add hours on ${day}`} onClick={() => addRangeToDay(weekday)}>
                          <PlusOutlined />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy these hours to other days">
                        <IconButton size="small" aria-label={`Copy ${day} hours`} onClick={(event) => openCopy(event, weekday)}>
                          <CopyOutlined />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
              );
            })}
          </Box>
          <Popover
            open={Boolean(copySource)}
            anchorEl={copySource?.anchor}
            onClose={() => setCopySource(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {copySource && (
              <Box sx={{ p: 1.5, width: 220 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Copy {WEEKDAYS[copySource.weekday]} hours to
                </Typography>
                <FormControlLabel
                  label="All days"
                  control={
                    <Checkbox
                      size="small"
                      checked={copyTargets.length === WEEKDAYS.length - 1}
                      indeterminate={copyTargets.length > 0 && copyTargets.length < WEEKDAYS.length - 1}
                      onChange={(event) => setCopyTargets(
                        event.target.checked ? WEEKDAYS.map((_, weekday) => weekday).filter((weekday) => weekday !== copySource.weekday) : []
                      )}
                    />
                  }
                />
                <Divider sx={{ my: 0.5 }} />
                {WEEKDAYS.map((day, weekday) => weekday !== copySource.weekday && (
                  <FormControlLabel
                    key={day}
                    sx={{ display: 'flex' }}
                    label={day}
                    control={
                      <Checkbox
                        size="small"
                        checked={copyTargets.includes(weekday)}
                        onChange={(event) => setCopyTargets((targets) => (
                          event.target.checked ? [...targets, weekday] : targets.filter((target) => target !== weekday)
                        ))}
                      />
                    }
                  />
                ))}
                <Button fullWidth variant="contained" size="small" sx={{ mt: 1 }} disabled={!copyTargets.length} onClick={applyCopy}>
                  Apply
                </Button>
              </Box>
            )}
          </Popover>
        </Stack>

        <Divider />
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box>
              <Typography variant="h6">Date overrides</Typography>
              <Typography variant="caption" color="text.secondary">Close a date or use different hours</Typography>
            </Box>
            <Button size="small" variant="outlined" startIcon={<PlusOutlined />} onClick={addOverride}>
              Add date
            </Button>
          </Stack>
          {typeof errors?.date_overrides === 'string' && (
            <Typography variant="caption" color="error">{errors.date_overrides}</Typography>
          )}
          {overrides.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No date overrides. Add one for holidays, leave or special hours.
            </Typography>
          ) : (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {overrides.map((override, index) => (
                <Stack
                  key={index}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ md: 'center' }}
                  sx={{ px: 1.5, py: 1.25, '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }}
                >
                  <TextField
                    size="small"
                    label="Date"
                    type="date"
                    value={override.date || ''}
                    inputProps={{ min: minimumOverrideDate }}
                    onChange={(event) => updateOverride(index, { date: event.target.value })}
                    error={Boolean(errors?.date_overrides?.[index]?.date)}
                    helperText={errors?.date_overrides?.[index]?.date}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 170, flexShrink: 0 }}
                  />
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={override.is_closed ? 'closed' : 'hours'}
                    onChange={(_event, mode) => mode && updateOverride(
                      index,
                      mode === 'closed'
                        ? { is_closed: true, start_time: '', end_time: '' }
                        : { is_closed: false, start_time: override.start_time || DEFAULT_RANGE.start_time, end_time: override.end_time || DEFAULT_RANGE.end_time }
                    )}
                    sx={{ flexShrink: 0 }}
                  >
                    <ToggleButton value="closed" sx={{ px: 1.5 }}>Closed</ToggleButton>
                    <ToggleButton value="hours" sx={{ px: 1.5 }}>Custom hours</ToggleButton>
                  </ToggleButtonGroup>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    {!override.is_closed && (
                      <TimeRange range={override} onChange={(next) => updateOverride(index, next)} />
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Tooltip title="Duplicate to the next day">
                      <IconButton size="small" aria-label="Duplicate date override" onClick={() => duplicateOverride(index)}>
                        <CopyOutlined />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Remove date override"
                        onClick={() => saveOverrides(overrides.filter((_, itemIndex) => itemIndex !== index))}
                      >
                        <DeleteOutlined />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              ))}
            </Box>
          )}
        </Stack>
      </Stack>
    </Grid>
  );
}

DoctorBookingFields.propTypes = {
  booking: PropTypes.shape({
    practice_start_year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    years_of_experience: PropTypes.number,
    profile_photo: PropTypes.string,
    consultation_fee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    old_patient_fee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    follow_up_fee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    online_booking_enabled: PropTypes.bool.isRequired,
    slot_duration_minutes: PropTypes.number.isRequired,
    max_online_bookings_per_day: PropTypes.number.isRequired,
    weekly_availability: PropTypes.array.isRequired,
    date_overrides: PropTypes.array.isRequired,
    recommended_slot_duration: PropTypes.object
  }).isRequired,
  errors: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  onPhotoChange: PropTypes.func.isRequired
};
